import { motion } from "framer-motion";
import { Eyebrow, FadeUp } from "@/components/site/Reveal";
import { scrollToId } from "@/components/site/SmoothScroll";
import { SERVICES } from "@/data/content";
import { ArrowUpRight } from "lucide-react";

const Card = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <motion.article
      data-testid={`service-card-${s.id}`}
      initial={{ opacity: 0.001, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
      className={`group relative overflow-hidden rounded-[28px] bg-[#14083E] min-h-[320px] lg:min-h-0 ${s.span}`}>
      <img src={s.img} alt={s.title} loading="lazy"
        className="absolute inset-0 h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1200ms] ease-out" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0424] via-[#0B0424]/55 to-[#0B0424]/5" />
      <div className="absolute inset-0 bg-[#E5157A]/0 group-hover:bg-[#E5157A]/15 transition-colors duration-500" />
      <div className="relative h-full flex flex-col justify-between p-6 sm:p-7 text-white">
        <div className="flex items-start justify-between">
          <span className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 grid place-items-center group-hover:bg-brand-gradient transition-colors duration-500">
            <Icon className="w-5 h-5" />
          </span>
          <span className="font-mono-x text-xs text-white/80">{s.tag}</span>
        </div>
        <div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{s.title}</h3>
          <p className="mt-2 text-sm sm:text-base text-white/85 leading-relaxed max-w-md">{s.text}</p>
          <button type="button" data-testid={`service-request-${s.id}`}
            onClick={() => { window.dispatchEvent(new CustomEvent("img:request-service", { detail: s.title })); scrollToId("request-care"); }}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-4 py-2 text-sm font-semibold hover:bg-white hover:text-[#14083E] transition-colors">
            Request this <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

export default function Services() {
  return (
    <section id="services" className="relative py-24 sm:py-32 bg-[#FAFAFC]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <FadeUp className="lg:col-span-7">
            <Eyebrow>Complete patient care</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold tracking-[-0.03em] leading-[0.98] text-4xl sm:text-5xl lg:text-6xl text-[#14083E]">
              One desk for every <span className="text-brand-gradient">care decision.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5">
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">Instead of calling five different places, families call us once. We compare options across our partner network, book what fits and stay involved until the case is closed.</p>
            <button data-testid="services-request-care-btn" onClick={() => scrollToId("contact")}
              className="mt-6 inline-flex items-center gap-2 font-semibold text-[#14083E] border-b-2 border-[#E5157A] pb-1 hover:gap-3 transition-[gap]">
              Talk to a care advisor <ArrowUpRight className="w-4 h-4" />
            </button>
          </FadeUp>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[280px] gap-4 sm:gap-5">
          {SERVICES.map((s, i) => <Card key={s.id} s={s} i={i} />)}
        </div>
      </div>
    </section>
  );
}
